import React from 'react';
import dayjs from 'dayjs';

import {
  OrganizationStorage,
  Storage,
} from '@totem/components/administration/storageReporting/types';
import {
  formatPercent,
  formatStorage,
  getDeletedBytes,
  getNoBuildingBytes,
  getOldest,
  getRecentBytes,
} from '@totem/components/administration/storageReporting/utilities';
import KpiCard from '@totem/components/ui_components/widgets/KpiCard';

type Props = {
  organizations: OrganizationStorage[];
  isDeactivated: boolean;
  recentDays: number;
};

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: 10,
  },
};

const emptyArea = () => ({
  count: 0,
  bytes: 0,
  deletedCount: 0,
  deletedBytes: 0,
  recentCount: 0,
  recentBytes: 0,
});

const emptyStorage = (): Storage => ({
  documents: emptyArea(),
  events: emptyArea(),
  eventsCleared: emptyArea(),
  devices: emptyArea(),
});

const sumStorage = (organizations: OrganizationStorage[]) =>
  organizations.reduce((totals, organization) => {
    (Object.keys(totals) as (keyof Storage)[]).forEach((areaKey) => {
      const area = organization.storage[areaKey];
      const total = totals[areaKey];
      total.count += area.count;

      total.bytes += area.bytes;

      total.deletedCount += area.deletedCount;

      total.deletedBytes += area.deletedBytes;

      total.recentCount += area.recentCount;

      total.recentBytes += area.recentBytes;
      if (area.oldest && (!total.oldest || area.oldest < total.oldest)) {
        total.oldest = area.oldest;
      }
    });
    return totals;
  }, emptyStorage());

const StorageReportKpis = ({
  organizations,
  isDeactivated,
  recentDays,
}: Props) => {
  const totals = sumStorage(organizations);
  const totalBytes = organizations.reduce(
    (total, organization) => total + organization.totalBytes,
    0,
  );
  const withData = organizations.filter(
    (organization) => organization.totalBytes > 0,
  );
  const buildingCount = organizations.reduce(
    (total, organization) =>
      total +
      organization.buildings.filter((building) => building.buildingId).length,
    0,
  );
  const deletedBytes = getDeletedBytes(totals);
  const oldest = getOldest(totals);

  if (isDeactivated) {
    return (
      <div style={styles.grid}>
        <KpiCard
          label="Orgs holding data"
          value={`${withData.length} of ${organizations.length}`}
          sub={`${organizations.length - withData.length} hold nothing`}
        />
        <KpiCard
          label="Total footprint"
          value={formatStorage(totalBytes)}
          sub="All held by deactivated orgs"
        />
        <KpiCard
          label="Soft-deleted"
          value={formatPercent(deletedBytes, totalBytes)}
          sub={`${formatStorage(deletedBytes)} already marked deleted`}
        />
        <KpiCard
          label="Oldest record"
          value={oldest ? dayjs(oldest).format('YYYY-MM') : '—'}
          sub={
            oldest
              ? `${dayjs().diff(dayjs(oldest), 'month')} months old`
              : 'No records'
          }
        />
      </div>
    );
  }

  const noBuildingBytes = organizations.reduce(
    (total, organization) =>
      total +
      (organization.organizationId ? getNoBuildingBytes(organization) : 0),
    0,
  );
  const recentBytes = getRecentBytes(totals);

  return (
    <div style={styles.grid}>
      <KpiCard
        label="Total footprint"
        value={formatStorage(totalBytes)}
        sub={`${withData.length} orgs · ${buildingCount} ${buildingCount === 1 ? 'building' : 'buildings'}`}
      />
      <KpiCard
        label="Cleared events"
        value={formatPercent(totals.eventsCleared.bytes, totalBytes)}
        sub={`${formatStorage(totals.eventsCleared.bytes)} of the total`}
      />
      <KpiCard
        label={`Growth · ${recentDays} days`}
        value={`+${formatStorage(recentBytes)}`}
        sub={`${totals.eventsCleared.recentCount.toLocaleString()} cleared events added`}
      />
      <KpiCard
        label="Reclaimable"
        value={formatStorage(deletedBytes)}
        sub="Soft-deleted devices and documents"
      />
      <KpiCard
        label="No building"
        value={formatPercent(noBuildingBytes, totalBytes)}
        sub={`${formatStorage(noBuildingBytes)} not assigned to a building`}
      />
    </div>
  );
};

export default StorageReportKpis;
